// frontend/src/components/DeliveryAddressCard.js

import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Divider, Alert, Grid, Chip, InputAdornment
} from '@mui/material';
import {
  LocationOn, Edit, Save, Close, Phone, LocalShipping, Person, Notes
} from '@mui/icons-material';
import { manufacturingAPI } from '../services/api';
import { toast } from 'react-toastify';

export default function DeliveryAddressCard({ 
  order, 
  onUpdate,
  showContactInfo = true,
  showDxNotes = true,
  compact = false 
}) {
  const [editDeliveryDialog, setEditDeliveryDialog] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_name: '',
    delivery_contact_name: '',
    delivery_phone: '',
    delivery_line_1: '',
    delivery_line_2: '',
    delivery_line_3: '',
    delivery_city: '',
    delivery_county: '',
    delivery_postcode: '',
    dx_note_1: '',
    dx_note_2: '',
    dx_note_3: '',
  });
  const [savingDelivery, setSavingDelivery] = useState(false);

  // Check if delivery info exists
  const hasDeliveryInfo = () => {
    return order?.delivery_name || 
           order?.delivery_contact_name ||
           order?.delivery_phone ||
           order?.delivery_line_1 || 
           order?.delivery_city || 
           order?.delivery_postcode || 
           order?.delivery_address;
  };

  // Check if DX notes exist
  const hasDxNotes = () => {
    return order?.dx_note_1 || order?.dx_note_2 || order?.dx_note_3;
  };

  const openEditDeliveryDialog = () => {
    setDeliveryForm({
      delivery_name: order?.delivery_name || '',
      delivery_contact_name: order?.delivery_contact_name || '',
      delivery_phone: order?.delivery_phone || '',
      delivery_line_1: order?.delivery_line_1 || '',
      delivery_line_2: order?.delivery_line_2 || '',
      delivery_line_3: order?.delivery_line_3 || '',
      delivery_city: order?.delivery_city || '',
      delivery_county: order?.delivery_county || '',
      delivery_postcode: order?.delivery_postcode || '',
      dx_note_1: order?.dx_note_1 || '',
      dx_note_2: order?.dx_note_2 || '',
      dx_note_3: order?.dx_note_3 || '',
    });
    setEditDeliveryDialog(true);
  };

  const handleDeliveryFormChange = (field) => (event) => {
    setDeliveryForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSaveDeliveryInfo = async () => {
    setSavingDelivery(true);
    try {
      await manufacturingAPI.customerOrders.update(order.id, deliveryForm);
      toast.success('Delivery information updated');
      setEditDeliveryDialog(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to update delivery info:', error);
      toast.error(error.response?.data?.error || 'Failed to update delivery information');
    } finally {
      setSavingDelivery(false);
    }
  };

  if (!order) return null;

  return (
    <>
      <Card sx={{ mb: compact ? 0 : 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn color="primary" />
              {compact ? 'Delivery To' : 'Delivery Address'}
            </Typography>
            <Tooltip title="Edit Delivery Info">
              <IconButton size="small" color="primary" onClick={openEditDeliveryDialog}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {hasDeliveryInfo() ? (
            <Box>
              {/* Company/Location Name */}
              {order.delivery_name && (
                <Typography variant="body1" fontWeight="bold">
                  {order.delivery_name}
                </Typography>
              )}
              
              {/* Address Lines */}
              {order.delivery_line_1 && (
                <Typography variant="body2">{order.delivery_line_1}</Typography>
              )}
              {order.delivery_line_2 && (
                <Typography variant="body2">{order.delivery_line_2}</Typography>
              )}
              {order.delivery_line_3 && (
                <Typography variant="body2">{order.delivery_line_3}</Typography>
              )}
              {order.delivery_city && (
                <Typography variant="body2">{order.delivery_city}</Typography>
              )}
              {order.delivery_county && (
                <Typography variant="body2">{order.delivery_county}</Typography>
              )}
              {order.delivery_postcode && (
                <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                  {order.delivery_postcode}
                </Typography>
              )}

              {/* Fallback to legacy delivery_address if no structured fields */}
              {!order.delivery_name && !order.delivery_line_1 && !order.delivery_city && order.delivery_address && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {order.delivery_address}
                </Typography>
              )}

              {/* Contact Information */}
              {(order.delivery_contact_name || order.delivery_phone) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Delivery Contact
                  </Typography>
                  
                  {order.delivery_contact_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="bold">
                        {order.delivery_contact_name}
                      </Typography>
                    </Box>
                  )}
                  
                  {order.delivery_phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">
                        {order.delivery_phone}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Legacy Contact Info (BOOKIN codes) - only show if no new contact fields */}
              {showContactInfo && !order.delivery_contact_name && !order.delivery_phone && 
               order.booking_contacts && order.booking_contacts.length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">Contact</Typography>
                  </Box>
                  {order.booking_contacts.map((contact, idx) => (
                    <Typography key={idx} variant="body2" fontWeight="bold">
                      {contact}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* DX Delivery Notes */}
              {showDxNotes && hasDxNotes() && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Notes fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">DX Delivery Notes</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {order.dx_note_1 && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Chip label="1" size="small" color="info" sx={{ minWidth: 20, height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="body2">{order.dx_note_1}</Typography>
                      </Box>
                    )}
                    {order.dx_note_2 && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Chip label="2" size="small" color="info" sx={{ minWidth: 20, height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="body2">{order.dx_note_2}</Typography>
                      </Box>
                    )}
                    {order.dx_note_3 && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Chip label="3" size="small" color="info" sx={{ minWidth: 20, height: 20, fontSize: '0.7rem' }} />
                        <Typography variant="body2">{order.dx_note_3}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <LocationOn color="disabled" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No delivery information
              </Typography>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={openEditDeliveryDialog}
                sx={{ mt: 1 }}
              >
                Add Delivery Info
              </Button>
              <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                Please add delivery address before shipping.
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit Delivery Info Dialog */}
      <Dialog 
        open={editDeliveryDialog} 
        onClose={() => setEditDeliveryDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping color="primary" />
            Edit Delivery Information
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Deliver To Section */}
            <Typography variant="subtitle2" color="text.secondary">
              Deliver To
            </Typography>
            <TextField
              fullWidth
              label="Company / School Name"
              value={deliveryForm.delivery_name}
              onChange={handleDeliveryFormChange('delivery_name')}
              placeholder="e.g. Pharmacy2U Ltd"
            />
            
            <Divider sx={{ my: 1 }} />
            
            {/* Address Section */}
            <Typography variant="subtitle2" color="text.secondary">
              Address
            </Typography>
            <TextField
              fullWidth
              label="Address Line 1"
              value={deliveryForm.delivery_line_1}
              onChange={handleDeliveryFormChange('delivery_line_1')}
              placeholder="e.g. Unit 4B Victoria Industrial Park"
            />
            <TextField
              fullWidth
              label="Address Line 2"
              value={deliveryForm.delivery_line_2}
              onChange={handleDeliveryFormChange('delivery_line_2')}
              placeholder="Optional"
            />
            <TextField
              fullWidth
              label="Address Line 3"
              value={deliveryForm.delivery_line_3}
              onChange={handleDeliveryFormChange('delivery_line_3')}
              placeholder="Optional"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={deliveryForm.delivery_city}
                  onChange={handleDeliveryFormChange('delivery_city')}
                  placeholder="e.g. Leeds"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="County"
                  value={deliveryForm.delivery_county}
                  onChange={handleDeliveryFormChange('delivery_county')}
                  placeholder="e.g. West Yorkshire"
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Postcode"
              value={deliveryForm.delivery_postcode}
              onChange={handleDeliveryFormChange('delivery_postcode')}
              placeholder="e.g. LS14 2LA"
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
            
            <Divider sx={{ my: 1 }} />
            
            {/* Contact Section */}
            <Typography variant="subtitle2" color="text.secondary">
              Delivery Contact
            </Typography>
            <TextField
              fullWidth
              label="Contact Name"
              value={deliveryForm.delivery_contact_name}
              onChange={handleDeliveryFormChange('delivery_contact_name')}
              placeholder="e.g. John Smith"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={deliveryForm.delivery_phone}
              onChange={handleDeliveryFormChange('delivery_phone')}
              placeholder="e.g. 0113 123456"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                )
              }}
            />

            <Divider sx={{ my: 1 }} />

            {/* DX Notes Section */}
            <Typography variant="subtitle2" color="text.secondary">
              DX Delivery Notes
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              These notes will be included in DX shipping consignments
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="1" size="small" color="info" sx={{ minWidth: 24, height: 24 }} />
              <TextField
                fullWidth
                label="DX Note 1"
                value={deliveryForm.dx_note_1}
                onChange={handleDeliveryFormChange('dx_note_1')}
                placeholder="e.g. ADD NOTE ON DX"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="2" size="small" color="info" sx={{ minWidth: 24, height: 24 }} />
              <TextField
                fullWidth
                label="DX Note 2"
                value={deliveryForm.dx_note_2}
                onChange={handleDeliveryFormChange('dx_note_2')}
                placeholder="e.g. ACCESS VIA LOW BRIDGE"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="3" size="small" color="info" sx={{ minWidth: 24, height: 24 }} />
              <TextField
                fullWidth
                label="DX Note 3"
                value={deliveryForm.dx_note_3}
                onChange={handleDeliveryFormChange('dx_note_3')}
                placeholder="e.g. HEIGHT 2.74M"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditDeliveryDialog(false)} 
            startIcon={<Close />}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveDeliveryInfo}
            disabled={savingDelivery}
            startIcon={savingDelivery ? null : <Save />}
          >
            {savingDelivery ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}